/**
 * Route path constants
 * Centralized route definitions to prevent hardcoded URLs throughout the codebase
 */

const openPageDetails =
  (page: string) =>
  ({
    pageId,
    searchParams,
    details,
    chat,
  }: {
    pageId: string;
    searchParams?: string;
    details?: boolean;
    chat?: boolean;
  }) => {
    const params = new URLSearchParams(searchParams);
    params.delete('details');
    params.delete('chat');
    if (details) params.set('details', 'true');
    if (chat) params.set('chat', 'true');
    return `${page}/${pageId}?${params.toString()}`;
  };

/**
 * Optional search parameters on a Knowledge Base document link. Connie's
 * Library links set both: the PDF opens at `page` and Ask Tia starts a new
 * chat instead of reopening the member's last one.
 */
export const KNOWLEDGE_BASE_DOCUMENT_PARAMS = {
  page: 'page',
  newChat: 'newChat',
} as const;

export const ROUTES = {
  DASHBOARD: '/',
  SIGNIN: '/signin',
  REGISTER: '/signup/register',
  INVITE_ACCEPT: '/invite/accept',
  COMPANY_ROOT: '/signup/company',
  COMPANY_PENDING: '/signup/company/pending',
  COMPANY_CREATE: '/signup/company/create',
  COMPANY_BC_SETUP: '/signup/company/bigchange-setup',
  JOBS: '/jobs',
  JOB_DETAILS: (jobId: string) => `/jobs/${jobId}`,
  SEARCH: '/search',
  KNOWLEDGE_BASE: '/knowledge',
  KNOWLEDGE_BASE_DOCUMENT: openPageDetails('/knowledge'),
  PROMPTS: '/prompts',
  BULLETINS: '/bulletins',
  ENGINEERS: '/engineers',
  VEHICLES: '/vehicles',
  RESOURCES: '/resources',
  AGENTS: '/agents',
  MESSAGES: '/messages',
  CHATS: '/chats',
  CALLS: '/calls',
  WORKFLOWS: '/workflows',
  SALES: '/sales',
  SALES_PERFORMANCE_DETAILS: openPageDetails('/sales'),
  QUOTE_DOCUMENTS: '/quoting/documents',
  QUOTE_DOCUMENT_DETAILS: (documentId: string) =>
    `/quoting/documents/${documentId}`,
  INTEGRATIONS: '/integrations',
  INTEGRATIONS_CONNECT: (integrationType: string) =>
    `/integrations/connect/${integrationType}`,
  INTEGRATIONS_UPLOAD: '/integrations/upload-csv',
  SETTINGS: '/settings',
  WORKSHEET_PASS: '/settings/worksheet-pass',
  WORKSHEET_PASS_WORKSHEET: (formRef: string) =>
    `/settings/worksheet-pass/${encodeURIComponent(formRef)}`,
  SUPPORT: '/support',
  CONTACTS: '/contacts',
  CASES: '/cases',
  CASE_DETAILS: (caseId: number | string) => `/cases/${caseId}`,
  PORTAL_JOBS: '/portal-jobs',
  PORTAL_JOBS_OVERVIEW: '/portal-jobs/overview',
  PORTAL_JOB_DETAILS: (caseId: number | string) => `/portal-jobs/${caseId}`,
  TEAMS: '/teams',
  TEAM_DETAILS: (teamId: number | string) => `/teams/${teamId}`,
  NOTIFICATIONS: '/notifications',
  PROFILE: '/profile',
} as const;
